import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import {
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
  Paper,
  Grid,
  Chip,
  CircularProgress,
  Alert,
  AlertTitle,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  ToggleButton,
  ToggleButtonGroup,
  Checkbox,
  FormControlLabel,
  FormHelperText,
  FormGroup,
  Switch,
  Tabs,
  Tab,
  IconButton,
  Link,
  Menu,
  Tooltip,
  InputAdornment,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import WarningIcon from '@mui/icons-material/Warning';
import SaveIcon from '@mui/icons-material/Save';
import ScheduleIcon from '@mui/icons-material/Schedule';
import SendIcon from '@mui/icons-material/Send';
import CodeIcon from '@mui/icons-material/Code';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import VisibilityIcon from '@mui/icons-material/Visibility';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CancelIcon from '@mui/icons-material/Cancel';
import ReplayIcon from '@mui/icons-material/Replay';
import { useSearchParams } from 'react-router-dom';
import PromotionStatusPanel from './PromotionStatusPanel';
import { api, fetchData } from '../../helpers/apiUtils';
import {
  CampaignWithEmail,
  PlxBewerbbarkeitDetail,
  PlxProduct,
  PlxWkzConfig,
  PlxClient,
  EmailFormData,
  StalenessFlagsResponse,
  StalenessKind,
} from '../../helpers/plxMailingData';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { validatePlxEmail, ValidationResult } from '../../helpers/emailValidation';
import SpamScorePanel, { CombinedResult as SpamScoreResult } from '../../components/SpamScorePanel';
import { recordDecision } from '../../helpers/spamWords/spamScoreApi';
import TinyMCEEditor, { TinyMCEEditorRef } from '../../components/TinyMCEEditor';
import EmailPreviewDialog from '../../components/EmailPreviewDialog';
import TestEmailDialog, { TestEmailClient } from '../../components/TestEmailDialog';
import ScheduleConfirmationDialog, {
  MailingPolicyTable,
} from '../../components/ScheduleConfirmationDialog';
import InfoTip from '../../components/InfoTip';
import NonOpenerSection from '../../components/NonOpenerSection';
import PlaceholderLegend from './PlaceholderLegend';
import CopyRefreshPanel from './CopyRefreshPanel';
import Editor from '@monaco-editor/react';
import moment from 'moment-timezone';
import { formatGermanTime, germanNow, toGermanDatetimeLocal } from '../../helpers/germanTime';
import { useUrlValidation } from '../../hooks/useUrlValidation';
import { containsHardcodedYear, YEAR_WARNING } from '../../helpers/mailingValidation';

// Every PLX mailing fans out to FT (Iterable) + external Mailchimp audiences.
// bg/sw/be/tt are gated to the GEV ⨯ I cross-subscriber pool at schedule time;
// kme and dnv send to their full audience (standalone MC clients, no gev/i dup
// tags; both capped at 2 emails per campaign, dnv additionally yields to its
// weekly SAM main day). Mirrors PLX_FANOUT_CLIENT_CODES in
// node-app/src/services/PlxMailingService.ts.
const PLX_FANOUT_CLIENT_CODES = ['ft', 'bg', 'sw', 'be', 'tt', 'kme', 'dnv'] as const;

// German label for the most serious staleness flag kind — used in the resend
// editor's Copy-Refresh banner. Keep in sync with StalenessKind in plxMailingData.
function describeStalenessKind(kind: StalenessKind | null): string {
  switch (kind) {
    case 'date_absolute':
      return 'Datums-Anker';
    case 'date_numeric':
      return 'Datums-Anker';
    case 'quarter':
      return 'Quartals-Bezüge';
    case 'forecast_horizon':
      return 'Prognose-Horizonte';
    case 'relative_day':
      return 'Tages-Bezüge';
    default:
      return 'Stellen';
  }
}

interface CampaignEditorProps {
  campaignId: number | null;
  onSaved: () => void;
  onCancel: () => void;
}

const getDefaultEmail = (clients: PlxClient[], mailingNumber = 1): EmailFormData => ({
  mailing_number: mailingNumber,
  subject: '',
  from_name: 'Finanztrends',
  preview_text: '',
  html_body: '',
  scheduled_datetime: moment
    .tz('Europe/Berlin')
    .add(mailingNumber, 'days')
    .set({ hour: 9, minute: 0 })
    .format('YYYY-MM-DDTHH:mm'),
  use_client_from_names: false,
  client_from_names: null,
  // Auto-fan-out: every email targets the full client list. User can no longer
  // toggle individual clients; backend re-derives at schedule time.
  selected_clients: [...PLX_FANOUT_CLIENT_CODES],
  auto_non_opener: false,
  auto_non_opener_time: '19:00',
  auto_non_opener_clients: null,
});

/** Normalise the campaign's target_clients column (string | array | null) to string[] | null. */
const parseTargetClients = (raw: unknown): string[] | null => {
  if (Array.isArray(raw)) return raw as string[];
  if (typeof raw === 'string' && raw.trim()) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) return parsed as string[];
    } catch {
      /* malformed — treat as standard fan-out */
    }
  }
  return null;
};

type EditorMode = 'wysiwyg' | 'code';

const CampaignEditor: React.FC<CampaignEditorProps> = ({ campaignId, onCancel }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  // Resend / series state
  const [parentCampaignId, setParentCampaignId] = useState<number | null>(null);
  const [parentName, setParentName] = useState<string | null>(null);
  const [sendNumber, setSendNumber] = useState<number>(1);
  const [seriesSize, setSeriesSize] = useState<number>(1);
  const [seriesRootId, setSeriesRootId] = useState<number | null>(null);
  const [resendDialogOpen, setResendDialogOpen] = useState(false);
  const [resendFirstSendDate, setResendFirstSendDate] = useState<string>('');
  const [resendLoading, setResendLoading] = useState(false);

  // Stale-copy detection (Copy-Refresh feature). Only populated for resend children
  // (parent_campaign_id IS NOT NULL). Refreshed whenever the active email changes
  // or the user persists edits. Banner renders only when totalCount > 0.
  const [stalenessFlags, setStalenessFlags] = useState<StalenessFlagsResponse | null>(null);
  const [stalenessFlagsLoading, setStalenessFlagsLoading] = useState(false);
  const [copyRefreshDismissed, setCopyRefreshDismissed] = useState(false);
  const [copyRefreshOpen, setCopyRefreshOpen] = useState(false);
  // Snapshot of the loaded campaign + emails — used to detect whether the user
  // edited anything before clicking *Erneut senden*. If untouched, we skip the
  // auto-save (which would otherwise hit validation rules added after the
  // original campaign was authored, e.g. the hardcoded-year guard).
  const loadedSnapshotRef = useRef<string>('');

  // Editor mode
  const [editorMode, setEditorMode] = useState<EditorMode>(() => {
    const saved = localStorage.getItem('plx-mailing-editor-mode');
    return saved === 'code' || saved === 'wysiwyg' ? saved : 'wysiwyg';
  });
  const [hasImportedHtml, setHasImportedHtml] = useState(false);
  const [showWysiwygWarning, setShowWysiwygWarning] = useState(false);

  // DOI-page-ad creative refresh — only meaningful when landing_url points at
  // an aufzeichnung LP. The button triggers an immediate scrape; the row in
  // doi_evergreen_creatives is otherwise refreshed nightly by
  // EvergreenCreativeRefreshService.
  const [doiCreativeRefreshing, setDoiCreativeRefreshing] = useState(false);
  const [doiCreativeStatus, setDoiCreativeStatus] = useState<string | null>(null);

  // Editor refs
  const editorRef = useRef<TinyMCEEditorRef | null>(null);
  const monacoRef = useRef<any>(null);

  // Campaign data
  const [name, setName] = useState('');
  const [productId, setProductId] = useState<number | ''>('');
  const [wkzCode, setWkzCode] = useState<string>('');
  const [concept, setConcept] = useState<string>('');
  const [landingUrl, setLandingUrl] = useState('');
  // Letzter Werbetag (JJJJ-MM-TT, einschließlich); '' = unbefristet. Der Newsletter-Feed gibt ihn an
  // ai-prod weiter, das die Kampagne ab dem Folgetag nicht mehr auswählt.
  const [validUntil, setValidUntil] = useState('');
  const [bewerbbarkeit, setBewerbbarkeit] = useState<PlxBewerbbarkeitDetail | null>(null);
  // Opt-in client targeting. null = standard auto-fan-out (ft/bg/sw/be/tt/kme);
  // an array = send only to those clients.
  const [targetClients, setTargetClients] = useState<string[] | null>(null);
  // Welcome-Variante: belegt eine Position der Welcome-Serie. Die Kampagne
  // bleibt eine normale PLX-Kampagne — dadurch funktionieren Attribution,
  // {PRODUCT_LINK} und der Welcome-Versand ohne zweite Datenhaltung.
  const [welcomePosition, setWelcomePosition] = useState<number | ''>('');
  const [welcomeAbTest, setWelcomeAbTest] = useState(false);
  // Getrennt von welcome_position: die Position sagt WO die Copy hingehört, dieser
  // Schalter OB sie schon rausgehen darf. Ein Klon startet inaktiv, weil er bis
  // zur ersten Überarbeitung byte-identisch zur Vorlage ist.
  const [welcomeCopyActive, setWelcomeCopyActive] = useState(false);
  const { validateUrl, getState, resetAll, hasUnreachableUrls, isValidating } = useUrlValidation();
  const landingUrlState = getState('landing_url');
  const [emails, setEmails] = useState<EmailFormData[]>([]);
  const [activeEmailTab, setActiveEmailTab] = useState(0);

  // Computed per-email status (not campaign-wide)
  const activeEmail = emails[activeEmailTab];
  const isEmailSent =
    activeEmail?.status === 'sent' || activeEmail?.schedule_statuses?.includes('sent') || false;
  const isEmailScheduled =
    activeEmail?.schedule_statuses?.split(',').some((pair: string) => {
      const status = pair.split(':')[1];
      return status === 'pending' || status === 'scheduled';
    }) || false;

  // Reference data
  const [products, setProducts] = useState<PlxProduct[]>([]);
  const [wkzConfigs, setWkzConfigs] = useState<PlxWkzConfig[]>([]);
  const [clients, setClients] = useState<PlxClient[]>([]);
  const [mailingPolicy, setMailingPolicy] = useState<MailingPolicyTable | null>(null);

  // Import modal state
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [importedContent, setImportedContent] = useState<string | null>(null);
  const [detectedUrls, setDetectedUrls] = useState<string[]>([]);
  const [selectedUrls, setSelectedUrls] = useState<string[]>([]);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Warnungen aus dem Import (Template-Chrome im extrahierten HTML). Nicht leer
  // = der Versand würde den Body ablehnen, also erst nach expliziter Bestätigung
  // übernehmen.
  const [importWarnings, setImportWarnings] = useState<string[]>([]);
  const [importWarningAck, setImportWarningAck] = useState(false);

  // Preview modal state
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewHtml, setPreviewHtml] = useState('');
  const [previewWarnings, setPreviewWarnings] = useState<string[]>([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewClient, setPreviewClient] = useState('ft');

  // Test email state
  const [testEmailOpen, setTestEmailOpen] = useState(false);

  // Schedule dialog state
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);

  // Cancel schedules state
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelScope, setCancelScope] = useState<'email' | 'campaign'>('email');
  const [cancelEmailId, setCancelEmailId] = useState<number | null>(null);
  const [cancelling, setCancelling] = useState(false);
  const [emailMenuAnchor, setEmailMenuAnchor] = useState<null | HTMLElement>(null);
  const [emailMenuIndex, setEmailMenuIndex] = useState<number>(0);

  // Validation state
  const [validation, setValidation] = useState<ValidationResult>({
    valid: true,
    errors: [],
    warnings: [],
  });
  const validationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Spam-score state (per email)
  const [spamResults, setSpamResults] = useState<(SpamScoreResult | null)[]>([]);
  const [spamOverrideOpen, setSpamOverrideOpen] = useState(false);
  const [spamOverridePayload, setSpamOverridePayload] = useState<{
    emailIndex: number;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const [spamAdvisoryOpen, setSpamAdvisoryOpen] = useState(false);
  const [spamAdvisoryPayload, setSpamAdvisoryPayload] = useState<{
    emailIndex: number;
    warningCount: number;
    llmChecked: boolean;
    resolve: (ok: boolean) => void;
  } | null>(null);
  // Soft-Guard der Welcome-Serie: Produkt- oder Themen-Doppelung ist erlaubt,
  // muss aber bewusst quittiert werden (Betreff-Doppelung bleibt hart verboten).
  const [diversityOpen, setDiversityOpen] = useState(false);
  const [diversityPayload, setDiversityPayload] = useState<{
    message: string;
    resolve: (ok: boolean) => void;
  } | null>(null);
  const handleSpamResultChange = useCallback((index: number, result: SpamScoreResult) => {
    setSpamResults((prev) => {
      const next = [...prev];
      next[index] = result;
      return next;
    });
  }, []);

  // Load reference data
  useEffect(() => {
    const loadReferenceData = async () => {
      try {
        const [productRes, clientRes] = await Promise.all([
          fetchData<PlxProduct[]>('/api/plx-mailings/products'),
          fetchData<PlxClient[]>('/api/plx-mailings/clients'),
        ]);

        if (productRes.success && productRes.data) {
          setProducts(productRes.data);
        }
        if (clientRes.success && clientRes.data) {
          setClients(clientRes.data);
          const extra = clientRes as unknown as { policy?: MailingPolicyTable };
          setMailingPolicy(extra.policy ?? null);
          if (!campaignId) {
            setEmails([getDefaultEmail(clientRes.data, 1)]);
          }
        }
      } catch (err) {
        console.error('Error loading reference data:', err);
      }
    };

    loadReferenceData();
  }, [campaignId]);

  // Load WKZ configs when product changes
  useEffect(() => {
    if (productId) {
      loadWkzConfigs(productId as number);
    } else {
      setWkzConfigs([]);
      setWkzCode('');
    }
  }, [productId]);

  const loadWkzConfigs = async (id: number) => {
    try {
      const response = await fetchData<PlxWkzConfig[]>(`/api/plx-mailings/products/${id}/wkz`);
      if (response.success && response.data) {
        setWkzConfigs(response.data);
      }
    } catch (err) {
      console.error('Error loading WKZ configs:', err);
    }
  };

  // Load existing campaign
  useEffect(() => {
    if (campaignId) {
      loadCampaign(campaignId);
    }
  }, [campaignId]);

  const loadCampaign = async (id: number) => {
    setLoading(true);
    setError(null);
    // Status gehört zum Datensatz — vor dem Laden leeren, sonst steht der der vorigen Kampagne da.
    setBewerbbarkeit(null);

    try {
      const response = await fetchData<CampaignWithEmail>(`/api/plx-mailings/campaigns/${id}`);
      if (response.success && response.data) {
        const campaign = response.data;
        setName(campaign.name);
        setProductId(campaign.product_id);
        setWkzCode(campaign.wkz_code || '');
        setConcept(campaign.concept || '');
        setLandingUrl(campaign.landing_url);
        setValidUntil(campaign.valid_until ? String(campaign.valid_until).slice(0, 10) : '');
        setBewerbbarkeit(
          campaign.bewerbbarkeit && 'mails' in campaign.bewerbbarkeit ? campaign.bewerbbarkeit : null,
        );
        setTargetClients(parseTargetClients(campaign.target_clients));
        setWelcomePosition(campaign.welcome_position ?? '');
        setWelcomeAbTest(Boolean(campaign.welcome_ab_test));
        setWelcomeCopyActive(Boolean(campaign.welcome_copy_active));
        setParentCampaignId(campaign.parent_campaign_id ?? null);
        setParentName(campaign.parent_name ?? null);
        setSendNumber(Number(campaign.send_number) || 1);
        setSeriesSize(Number(campaign.series_size) || 1);
        setSeriesRootId(campaign.series_root_id ?? campaign.id);

        if (campaign.emails && campaign.emails.length > 0) {
          const loadedEmails: EmailFormData[] = campaign.emails.map((e: any) => ({
            id: e.id,
            mailing_number: e.mailing_number,
            subject: e.subject,
            from_name: e.from_name,
            preview_text: e.preview_text || '',
            html_body: e.html_body,
            scheduled_datetime: toGermanDatetimeLocal(e.scheduled_datetime),
            use_client_from_names: e.use_client_from_names,
            client_from_names: e.client_from_names,
            // Ignore the legacy stored CSV — auto-fan-out is fixed at 5 clients.
            selected_clients: [...PLX_FANOUT_CLIENT_CODES],
            auto_non_opener: !!e.auto_non_opener,
            auto_non_opener_time: e.auto_non_opener_time
              ? e.auto_non_opener_time.substring(0, 5)
              : '19:00',
            auto_non_opener_clients: e.auto_non_opener_clients
              ? e.auto_non_opener_clients.split(',')
              : null,
            status: e.status,
            schedule_statuses: e.schedule_statuses,
          }));
          setEmails(loadedEmails);
          // Capture a snapshot of the persisted state so confirmResend can
          // detect whether the user edited anything before clicking *Erneut
          // senden*. If untouched, we skip the validation-laden auto-save.
          loadedSnapshotRef.current = JSON.stringify({
            name: campaign.name,
            product_id: campaign.product_id,
            wkz_code: campaign.wkz_code || '',
            concept: campaign.concept || '',
            landing_url: campaign.landing_url,
            valid_until: campaign.valid_until ? String(campaign.valid_until).slice(0, 10) : '',
            target_clients: parseTargetClients(campaign.target_clients),
            // Welcome-Felder gehören in den Snapshot: wer NUR die Position oder
            // den A/B-Haken ändert, gilt sonst als "nichts geändert" und
            // verliert die Änderung auf dem Resend-Autosave-Pfad.
            welcome_position: campaign.welcome_position ?? '',
            welcome_ab_test: Boolean(campaign.welcome_ab_test),
            welcome_copy_active: Boolean(campaign.welcome_copy_active),
            emails: loadedEmails,
          });
        } else {
          setEmails([getDefaultEmail(clients, 1)]);
          loadedSnapshotRef.current = '';
        }
      } else {
        setError('Kampagne konnte nicht geladen werden');
      }
    } catch (err) {
      setError('Fehler beim Laden der Kampagne');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  // Has the loaded campaign been edited since load? Used by confirmResend to
  // decide whether to run the auto-save (which fires validation, including the
  // hardcoded-year guard that legacy content predates).
  const hasUnsavedChanges = useCallback((): boolean => {
    if (!loadedSnapshotRef.current) return false;
    const current = JSON.stringify({
      name,
      product_id: productId,
      wkz_code: wkzCode,
      concept,
      landing_url: landingUrl,
      valid_until: validUntil,
      target_clients: targetClients,
      welcome_position: welcomePosition,
      welcome_ab_test: welcomeAbTest,
      welcome_copy_active: welcomeCopyActive,
      emails,
    });
    return current !== loadedSnapshotRef.current;
  }, [
    name,
    productId,
    wkzCode,
    concept,
    landingUrl,
    validUntil,
    targetClients,
    welcomePosition,
    welcomeAbTest,
    welcomeCopyActive,
    emails,
  ]);

  // Debounced validation
  const runValidation = useCallback((subject: string, htmlBody: string) => {
    if (validationTimeoutRef.current) {
      clearTimeout(validationTimeoutRef.current);
    }
    validationTimeoutRef.current = setTimeout(() => {
      const result = validatePlxEmail(subject, htmlBody);
      setValidation(result);
    }, 500);
  }, []);

  // Run validation when current email content changes
  useEffect(() => {
    const currentEmail = emails[activeEmailTab];
    if (currentEmail) {
      runValidation(currentEmail.subject, currentEmail.html_body);
    }
  }, [emails, activeEmailTab, runValidation]);

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (validationTimeoutRef.current) {
        clearTimeout(validationTimeoutRef.current);
      }
    };
  }, []);

  // Get current email for easier access
  const currentEmail = emails[activeEmailTab];

  // Fetch staleness flags for the currently active email — gated on
  //  (a) resend child (parentCampaignId !== null) — fresh originals don't need it,
  //  (b) email has been persisted (currentEmail.id is set) — drafts in memory only
  //      have nothing to detect against yet.
  // Race-safe: cancellation flag on each render of the effect.
  useEffect(() => {
    if (parentCampaignId === null) {
      setStalenessFlags(null);
      return;
    }
    const emailId = currentEmail?.id;
    if (!campaignId || !emailId) {
      setStalenessFlags(null);
      return;
    }
    let cancelled = false;
    setStalenessFlagsLoading(true);
    fetchData<StalenessFlagsResponse>(
      `/api/plx-mailings/campaigns/${campaignId}/emails/${emailId}/staleness-flags`,
    )
      .then((res) => {
        if (cancelled) return;
        if (res.success && res.data) setStalenessFlags(res.data);
        else setStalenessFlags(null);
      })
      .catch((err) => {
        if (!cancelled) console.error('[staleness-flags] fetch failed:', err);
      })
      .finally(() => {
        if (!cancelled) setStalenessFlagsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [campaignId, currentEmail?.id, parentCampaignId]);

  // Reset the "user dismissed banner" flag when the active email switches —
  // a fresh email is a fresh decision surface.
  useEffect(() => {
    setCopyRefreshDismissed(false);
  }, [currentEmail?.id, campaignId]);

  // Refetch preview when client changes (while dialog is open)
  const fetchPreviewForClient = useCallback(
    async (clientCode: string) => {
      if (!campaignId || !previewOpen) return;

      setPreviewLoading(true);
      try {
        const emailNumber = currentEmail?.mailing_number || 1;
        const response = await fetchData<{ html: string; warnings?: string[] }>(
          `/api/plx-mailings/campaigns/${campaignId}/preview?client=${clientCode}&email_number=${emailNumber}`,
        );
        if (response.success && response.data) {
          setPreviewHtml(response.data.html);
          setPreviewWarnings(response.data.warnings || []);
        }
      } catch (err) {
        console.error('Failed to fetch preview:', err);
      } finally {
        setPreviewLoading(false);
      }
    },
    [campaignId, previewOpen, currentEmail?.mailing_number],
  );

  // Effect to refetch preview when client changes while dialog is open
  useEffect(() => {
    if (previewOpen && campaignId) {
      fetchPreviewForClient(previewClient);
    }
  }, [previewClient, previewOpen, campaignId, fetchPreviewForClient]);

  // Get available clients for test email (filtered to email's selected_clients)
  const availableTestClients = useMemo((): TestEmailClient[] => {
    // Mirror the campaign's effective fan-out: the target_clients override when
    // set, otherwise the standard auto-fan-out. Keeps the test-send dialog in
    // sync with who the campaign actually sends to (incl. kme/dnv overrides).
    const codes =
      targetClients && targetClients.length > 0 ? targetClients : [...PLX_FANOUT_CLIENT_CODES];
    return clients
      .filter((c) => codes.includes(c.code))
      .map((c) => ({
        code: c.code,
        name: c.name,
        esp_type: c.esp_type,
      }));
  }, [clients, targetClients]);

  // Get current HTML content from editor
  const getCurrentHtml = (): string => {
    if (editorMode === 'code' && monacoRef.current) {
      return monacoRef.current.getValue() || '';
    } else if (editorRef.current) {
      return editorRef.current.getContent();
    }
    return currentEmail?.html_body || '';
  };

  // Helper to update current email
  const updateCurrentEmail = useCallback(
    (updates: Partial<EmailFormData>) => {
      setEmails((prev) => prev.map((e, i) => (i === activeEmailTab ? { ...e, ...updates } : e)));
    },
    [activeEmailTab],
  );

  // Add new email
  const addEmail = useCallback(() => {
    if (emails.length >= 5) return;
    const newNumber = emails.length + 1;
    setEmails((prev) => [...prev, getDefaultEmail(clients, newNumber)]);
    setActiveEmailTab(emails.length);
  }, [emails.length, clients]);

  // Remove email
  const removeEmail = useCallback(
    (index: number) => {
      if (emails.length <= 1) return;
      const newEmails = emails.filter((_, i) => i !== index);
      // Renumber remaining emails
      newEmails.forEach((e, i) => (e.mailing_number = i + 1));
      setEmails(newEmails);
      setActiveEmailTab(Math.min(activeEmailTab, newEmails.length - 1));
    },
    [emails, activeEmailTab],
  );

  // Handle editor mode change
  const handleEditorModeChange = (_: React.MouseEvent<HTMLElement>, newMode: EditorMode | null) => {
    if (!newMode) return;

    if (newMode === 'wysiwyg' && hasImportedHtml) {
      setShowWysiwygWarning(true);
      return;
    }

    // Save current content before switching
    const currentHtml = getCurrentHtml();
    if (currentEmail) {
      updateCurrentEmail({ html_body: currentHtml });
    }

    setEditorMode(newMode);
    localStorage.setItem('plx-mailing-editor-mode', newMode);
  };

  // Save campaign
  // skipNavigation: when true, suppress success message (used during scheduling)
  /**
   * Sendet eine Anfrage und wiederholt sie EINMAL mit `confirm_diversity: true`,
   * wenn das Backend den Soft-Guard der Welcome-Diversität meldet (409
   * `requires_confirmation`) — eine eigene Welcome-Copy, deren Produkt oder
   * Thema sich mit einer anderen Position der Serie überschneidet. Ohne diese
   * Wiederholung ist der 409 für Marketing eine Sackgasse.
   *
   * Bewusst nur die fehlgeschlagene Anfrage statt eines erneuten handleSave:
   * ein zweiter Durchlauf würde die Spam-Dialoge nochmal öffnen, die der Nutzer
   * gerade quittiert hat. Der harte Betreff-Guard (400) läuft hier nicht durch
   * und bleibt unbestätigbar.
   */
  const saveWithDiversityRetry = async <T,>(
    url: string,
    method: 'PUT' | 'POST',
    payload: Record<string, unknown>,
  ) => {
    const first = await fetchData<T>(url, { method, body: JSON.stringify(payload) });
    if (first.success || !first.requires_confirmation) return first;

    const confirmed = await new Promise<boolean>((resolve) => {
      setDiversityPayload({ message: first.error ?? '', resolve });
      setDiversityOpen(true);
    });
    if (!confirmed) return { ...first, error: 'Speichern abgebrochen' };

    return fetchData<T>(url, {
      method,
      body: JSON.stringify({ ...payload, confirm_diversity: true }),
    });
  };

  const handleSave = async (skipNavigation = false): Promise<boolean> => {
    if (!name.trim()) {
      setError('Bitte einen Kampagnennamen eingeben');
      return false;
    }
    if (!productId) {
      setError('Bitte ein Produkt auswählen');
      return false;
    }
    if (!landingUrl.trim()) {
      setError('Bitte eine Landing-URL eingeben');
      return false;
    }

    // Check for hardcoded years in subject/preview_text
    for (let i = 0; i < emails.length; i++) {
      const e = emails[i];
      if (!e.subject) continue;
      if (containsHardcodedYear(e.subject) || containsHardcodedYear(e.preview_text)) {
        setError(`E-Mail ${i + 1}: Jahreszahl erkannt — bitte {DATE:Y} als Platzhalter verwenden`);
        setActiveEmailTab(i);
        return false;
      }
    }

    // Spam-Score hard-block check (3+ red flags, L2 not checked or <30)
    const overriddenIndexes = new Set<number>();
    for (let i = 0; i < emails.length; i++) {
      const spam = spamResults[i];
      if (spam?.shouldHardBlock) {
        setActiveEmailTab(i);
        const userConfirmed = await new Promise<boolean>((resolve) => {
          setSpamOverridePayload({ emailIndex: i, resolve });
          setSpamOverrideOpen(true);
        });
        if (!userConfirmed) {
          setError(
            `E-Mail ${i + 1}: Content-Quality-Check nicht bestanden (${spam.layer1.redCount} Red-Flags) — bitte Begriffe ersetzen.`,
          );
          return false;
        }
        overriddenIndexes.add(i);
      }
    }

    // Spam-Score advisory (non-blocking): warn if L1 warnings present and
    // either no LLM check run, stale check, or LLM score < 70.
    for (let i = 0; i < emails.length; i++) {
      const spam = spamResults[i];
      if (!spam || spam.shouldHardBlock) continue;
      const warningCount = spam.layer1.flaggedPhrases.length + spam.layer1.patternHits.length;
      if (warningCount === 0) continue;
      const llmChecked = spam.layer2Score != null && !spam.contentHashStale;
      if (llmChecked && spam.layer2Score! >= 70) continue;
      setActiveEmailTab(i);
      const userProceed = await new Promise<boolean>((resolve) => {
        setSpamAdvisoryPayload({
          emailIndex: i,
          warningCount,
          llmChecked,
          resolve,
        });
        setSpamAdvisoryOpen(true);
      });
      if (!userProceed) return false;
    }

    setSaving(true);
    setError(null);
    if (!skipNavigation) {
      setSuccess(null);
    }

    try {
      // Get current HTML for active tab and update emails array
      const currentHtml = getCurrentHtml();
      const emailsToSave = emails.map((e, i) =>
        i === activeEmailTab ? { ...e, html_body: currentHtml } : e,
      );

      if (campaignId) {
        // Update existing campaign
        const updateRes = await saveWithDiversityRetry(
          `/api/plx-mailings/campaigns/${campaignId}`,
          'PUT',
          {
            name,
            product_id: productId,
            wkz_code: wkzCode || null,
            concept: concept || null,
            landing_url: landingUrl,
            valid_until: validUntil || null,
            target_clients: targetClients,
            welcome_position: welcomePosition === '' ? null : welcomePosition,
            welcome_ab_test: welcomeAbTest,
            welcome_copy_active: welcomeCopyActive,
          },
        );

        if (!updateRes.success) {
          throw new Error(updateRes.error || 'Fehler beim Speichern');
        }

        // Save all emails (batch upsert)
        const emailsWithContent = emailsToSave.filter((e) => e.subject);
        if (emailsWithContent.length > 0) {
          const emailRes = await saveWithDiversityRetry(
            `/api/plx-mailings/campaigns/${campaignId}/emails`,
            'POST',
            { emails: emailsWithContent },
          );
          if (!emailRes.success) {
            throw new Error(emailRes.error || 'Fehler beim Speichern der E-Mails');
          }
        }

        if (!skipNavigation) {
          setSuccess('Kampagne gespeichert');
        }
      } else {
        // Create new campaign
        const createRes = await fetchData<{ campaign_id: number }>('/api/plx-mailings/campaigns', {
          method: 'POST',
          body: JSON.stringify({
            name,
            product_id: productId,
            wkz_code: wkzCode || null,
            concept: concept || null,
            landing_url: landingUrl,
            valid_until: validUntil || null,
            target_clients: targetClients,
            welcome_position: welcomePosition === '' ? null : welcomePosition,
            welcome_ab_test: welcomeAbTest,
            // Muss mit: der DB-Default ist 0, und ohne dieses Feld sieht der
            // Nutzer nach dem ersten Speichern einen gesetzten Haken, während die
            // Copy inaktiv in der DB liegt und nicht rausgeht.
            welcome_copy_active: welcomeCopyActive,
          }),
        });

        if (!createRes.success || !createRes.data) {
          throw new Error(createRes.error || 'Fehler beim Erstellen');
        }

        const newCampaignId = createRes.data.campaign_id;

        // Save all emails (batch upsert)
        const emailsWithContent = emailsToSave.filter((e) => e.subject);
        if (emailsWithContent.length > 0) {
          const emailRes = await saveWithDiversityRetry(
            `/api/plx-mailings/campaigns/${newCampaignId}/emails`,
            'POST',
            { emails: emailsWithContent },
          );
          if (!emailRes.success) {
            throw new Error(emailRes.error || 'Fehler beim Speichern der E-Mails');
          }
        }

        if (!skipNavigation) {
          setSuccess('Kampagne erstellt');
        }
      }

      // Record user_decision for each email's spam check
      emailsToSave.forEach((e, i) => {
        const spam = spamResults[i];
        if (!spam) return;
        const decision = overriddenIndexes.has(i) ? 'overridden' : 'accepted';
        recordDecision('plx_campaign', e.mailing_number, decision, campaignId ?? null).catch(
          (err) => console.warn('recordDecision failed', err),
        );
      });
      return true;
    } catch (err) {
      setError((err as Error).message);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Schedule for ESP - open dialog
  const handleSchedule = () => {
    if (!campaignId || emails.length === 0) return;
    setScheduleDialogOpen(true);
  };

  // Schedulability check for each email
  const isEmailSchedulable = (email: {
    scheduled_datetime: string;
    selected_clients: string[];
    schedule_statuses?: string | null;
    status?: string;
  }) => {
    // scheduled_datetime traegt Berlin-Wallclock-Bytes: moment.utc() liest sie roh, germanNow()
    // ist "jetzt" im selben Koordinatensystem (plain moment() waere +2h -> 2h lang faelschlich Zukunft)
    const scheduled = moment.utc(email.scheduled_datetime);
    const hasClients = email.selected_clients?.length > 0;

    // Check for schedules already sent to ESP (scheduled = in ESP, sent = delivered)
    // Note: 'pending' means schedule record exists but NOT yet sent to ESP - this is reschedulable
    const hasEspSchedule =
      email.schedule_statuses?.split(',').some((pair) => {
        const status = pair.split(':')[1];
        return status === 'scheduled'; // Only block if actually scheduled in ESP
      }) || false;

    // Check if already sent
    const hasSent = email.status === 'sent' || email.schedule_statuses?.includes('sent');

    if (hasSent) return { schedulable: false, reason: 'bereits gesendet' };
    if (hasEspSchedule) return { schedulable: false, reason: 'bereits geplant' };
    if (!hasClients) return { schedulable: false, reason: 'keine Clients' };
    if (scheduled.isBefore(germanNow())) return { schedulable: false, reason: 'Vergangenheit' };
    return { schedulable: true };
  };

  // Execute scheduling from dialog
  const executeSchedule = async (emailNumbers: number[]) => {
    // First save current state (silently, skip success message)
    const saveSuccess = await handleSave(true);
    if (!saveSuccess) {
      throw new Error('Speichern fehlgeschlagen - Planung abgebrochen');
    }

    // Then schedule selected emails
    const response = await fetchData(`/api/plx-mailings/campaigns/${campaignId}/schedule`, {
      method: 'POST',
      body: JSON.stringify({ email_numbers: emailNumbers }),
    });

    if (!response.success) {
      throw new Error(response.error || 'Fehler beim Planen');
    }
  };

  // Cancel all schedules for an email
  const handleCancelEmailSchedules = (emailIndex: number) => {
    const email = emails[emailIndex];
    if (!email || !campaignId) return;

    // Need actual email ID from database, not mailing_number
    // emails array stores form data, we need to look up the DB record
    setCancelScope('email');
    setCancelEmailId(emailIndex); // We'll use index to identify which email
    setCancelDialogOpen(true);
    setEmailMenuAnchor(null);
  };

  // Cancel all schedules for entire campaign
  const handleCancelCampaignSchedules = () => {
    if (!campaignId) return;
    setCancelScope('campaign');
    setCancelDialogOpen(true);
  };

  // Execute the cancel operation
  const executeCancelSchedules = async () => {
    if (!campaignId) return;

    setCancelling(true);
    setError(null);

    try {
      let response;
      if (cancelScope === 'email' && cancelEmailId !== null) {
        // Get the email's database ID
        // The emails array has mailing_number but we need the actual email_id
        // We can get it by fetching the campaign data or passing it through
        // For now, use mailing_number as the API expects email position
        const emailNumber = cancelEmailId + 1; // Convert 0-indexed to 1-indexed mailing_number
        response = await fetchData(
          `/api/plx-mailings/campaigns/${campaignId}/emails/${emailNumber}/schedules`,
          {
            method: 'DELETE',
          },
        );
      } else {
        response = await fetchData(`/api/plx-mailings/campaigns/${campaignId}/all-schedules`, {
          method: 'DELETE',
        });
      }

      if (response.success) {
        const resultData = response as unknown as {
          cancelled: number;
          skipped: number;
        };
        const cancelled = resultData.cancelled || 0;
        const skipped = resultData.skipped || 0;
        let message = `${cancelled} Planung(en) gecancelt`;
        if (skipped > 0) {
          message += `, ${skipped} übersprungen (bereits gesendet)`;
        }
        setSuccess(message);
        setCancelDialogOpen(false);
        loadCampaign(campaignId);
      } else {
        setError(response.error || 'Fehler beim Abbrechen');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setCancelling(false);
    }
  };

  // Check if any email has active schedules (for campaign-level cancel button)
  const hasAnyActiveSchedules = emails.some((email) =>
    email.schedule_statuses?.split(',').some((pair) => {
      const status = pair.split(':')[1];
      return status === 'pending' || status === 'scheduled';
    }),
  );

  // Send all to ESP (with double confirmation for safety)
  const handleSendAllToEsp = async () => {
    if (!campaignId || emails.length === 0) return;

    // First confirmation - warn about scheduling
    if (
      !window.confirm(
        '⚠️ ACHTUNG: Direktes Senden sollte nur im Notfall verwendet werden!\n\n' +
          'Bitte nutze stattdessen "Bei ESP planen" für zeitgesteuerten Versand.\n\n' +
          'Trotzdem JETZT senden?',
      )
    )
      return;

    // Second confirmation - final safety check
    if (
      !window.confirm(
        '🚨 LETZTE WARNUNG 🚨\n\n' +
          'Die E-Mails werden SOFORT an alle Empfänger gesendet!\n' +
          'Dies kann NICHT rückgängig gemacht werden.\n\n' +
          'Wirklich JETZT senden?',
      )
    )
      return;

    setSaving(true);
    setError(null);

    try {
      const response = await fetchData(
        `/api/plx-mailings/campaigns/${campaignId}/send-all-to-esp`,
        {
          method: 'POST',
        },
      );

      if (response.success) {
        setSuccess('E-Mails an ESPs gesendet');
        loadCampaign(campaignId);
      } else {
        throw new Error(response.error || 'Fehler beim Senden');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  // Reset resend dialog state on campaign switch so the next open recomputes
  // a sensible default instead of carrying the prior campaign's date forward.
  // CampaignEditor stays mounted across `setSearchParams({id: newId})` after a
  // successful resend, so without this the default-date guard below would
  // skip the recalc and the dialog would reopen with a stale value.
  useEffect(() => {
    setResendFirstSendDate('');
    setResendDialogOpen(false);
  }, [campaignId]);

  // When the resend dialog opens, default the first-send date picker to a
  // sensible future point: max(earliest source send + 7 days, now + 1 day).
  // Guarded so a value supplied by the Resend-Empfehlungen tab (via the
  // `resend_to` URL param) survives the open and isn't overwritten.
  useEffect(() => {
    if (!resendDialogOpen || emails.length === 0) return;
    if (resendFirstSendDate) return;
    const earliestStr = emails
      .map((e) => e.scheduled_datetime)
      .filter(Boolean)
      .sort()[0];
    const earliestPlus7 = earliestStr
      ? moment.tz(earliestStr, 'Europe/Berlin').add(7, 'days')
      : null;
    const tomorrow = moment.tz('Europe/Berlin').add(1, 'day').set({ hour: 9, minute: 0 });
    const candidate = earliestPlus7 && earliestPlus7.isAfter(tomorrow) ? earliestPlus7 : tomorrow;
    setResendFirstSendDate(candidate.format('YYYY-MM-DDTHH:mm'));
  }, [resendDialogOpen, emails, resendFirstSendDate]);

  // Auto-trigger from /plx-mailings?tab=editor&id=N&resend_to=ISO — Resend-
  // Empfehlungen drops the user into the editor with the resend dialog
  // pre-opened and the suggested send-date pre-filled.
  useEffect(() => {
    const resendTo = searchParams.get('resend_to');
    if (!resendTo || emails.length === 0 || resendDialogOpen) return;
    setResendFirstSendDate(resendTo);
    setResendDialogOpen(true);
    const next = new URLSearchParams(searchParams);
    next.delete('resend_to');
    setSearchParams(next, { replace: true });
  }, [searchParams, emails, resendDialogOpen, setSearchParams]);

  // Resend — clones the campaign as the next send in its series, then opens the
  // new draft in the editor. Time-critical copy is edited on the new campaign;
  // attribution stays unique per email.id, and series stats roll up via
  // COALESCE(parent_campaign_id, id) on the backend.
  const confirmResend = async () => {
    if (!campaignId) return;
    setResendLoading(true);
    setError(null);
    try {
      // Save first ONLY if the user actually edited the loaded campaign.
      // Validation rules added after a campaign was authored (e.g. the
      // hardcoded-year guard) would otherwise block a no-edit resend on legacy
      // content. handleSave returns false on validation/URL/spam failure and
      // already surfaces the error via setError — abort in that case.
      if (hasUnsavedChanges()) {
        const saved = await handleSave(true /* skipNavigation: stay on editor */);
        if (!saved) {
          setResendLoading(false);
          return;
        }
      }

      // Send first_email_datetime so the backend can compute the time-shift
      // anchored to the user-picked date for email #1; remaining emails shift
      // by the same delta, preserving the original rhythm.
      const response = await fetchData<{
        id: number;
        send_number: number;
        emails_copied: number;
      }>(`/api/plx-mailings/campaigns/${campaignId}/resend`, {
        method: 'POST',
        body: JSON.stringify({ first_email_datetime: resendFirstSendDate }),
      });

      if (response.success && response.data) {
        const newId = response.data.id;
        setSuccess(
          `Send #${response.data.send_number} angelegt (${response.data.emails_copied} E-Mails kopiert)`,
        );
        setResendDialogOpen(false);
        // Navigate the editor to the new draft via URL params (parent owns ?tab=editor&id=)
        setSearchParams({ tab: 'editor', id: String(newId) });
      } else {
        throw new Error(response.error || 'Resend fehlgeschlagen');
      }
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setResendLoading(false);
    }
  };

  // Preview - opens dialog and fetches initial preview
  const handlePreview = async () => {
    if (!campaignId) {
      setError('Bitte Kampagne erst speichern');
      return;
    }

    if (!currentEmail?.html_body) {
      setError('Bitte erst HTML-Inhalt eingeben');
      return;
    }

    setPreviewLoading(true);
    setError(null);

    try {
      // Save first to ensure latest content is used
      await handleSave();

      // Set initial preview client based on selected clients (default to 'ft' or first selected)
      const initialClient = currentEmail.selected_clients?.includes('ft')
        ? 'ft'
        : currentEmail.selected_clients?.[0] || 'ft';
      setPreviewClient(initialClient);

      // Fetch preview for initial client and current email
      const emailNumber = currentEmail.mailing_number;
      const response = await fetchData<{ html: string }>(
        `/api/plx-mailings/campaigns/${campaignId}/preview?client=${initialClient}&email_number=${emailNumber}`,
      );
      if (response.success && response.data) {
        setPreviewHtml(response.data.html);
        setPreviewOpen(true);
      } else {
        setError(response.error || 'Fehler beim Laden der Vorschau');
      }
    } catch (err) {
      setError('Fehler beim Laden der Vorschau');
    } finally {
      setPreviewLoading(false);
    }
  };

  // Test email - callback for TestEmailDialog
  const handleSendTestEmail = async (
    testEmails: string[],
    clientCodes: string[],
  ): Promise<{ success: boolean; message?: string }> => {
    if (!campaignId) {
      return { success: false, message: 'Bitte Kampagne erst speichern' };
    }

    try {
      // Save first to ensure latest content is used
      await handleSave();

      // Send test for current email tab
      const emailNumber = currentEmail?.mailing_number || 1;
      const response = await fetchData<{
        results: { clientCode: string; success: boolean; error?: string }[];
      }>(`/api/plx-mailings/campaigns/${campaignId}/send-test`, {
        method: 'POST',
        body: JSON.stringify({
          test_emails: testEmails,
          client_codes: clientCodes,
          email_number: emailNumber,
        }),
      });

      if (response.success) {
        const successClients = response.data?.results?.filter((r) => r.success) || [];
        const failedClients = response.data?.results?.filter((r) => !r.success) || [];

        if (failedClients.length === 0) {
          setSuccess(
            `Test-E-Mail #${emailNumber} an ${testEmails.join(', ')} gesendet (Clients: ${successClients.map((c) => c.clientCode).join(', ')})`,
          );
          return { success: true };
        } else if (successClients.length > 0) {
          setSuccess(
            `Teilweise erfolgreich: ${successClients.map((c) => c.clientCode).join(', ')}`,
          );
          return {
            success: true,
            message: `Fehlgeschlagen bei: ${failedClients.map((c) => `${c.clientCode}: ${c.error}`).join('; ')}`,
          };
        } else {
          return {
            success: false,
            message: `Fehler bei allen Clients: ${failedClients.map((c) => `${c.clientCode}: ${c.error}`).join('; ')}`,
          };
        }
      } else {
        return {
          success: false,
          message: response.error || 'Fehler beim Senden',
        };
      }
    } catch (err) {
      return { success: false, message: (err as Error).message };
    }
  };

  // Import HTML file
  const handleImportFile = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    setImportLoading(true);
    setImportError(null);

    try {
      // Read file content
      const htmlContent = await file.text();

      const res = await fetch('/api/plx-mailings/import-template', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ htmlContent }),
      });
      const response = await res.json();

      if (response.success && response.data) {
        setImportedContent(response.data.extractedContent);
        setDetectedUrls(response.data.detectedUrls || []);
        setSelectedUrls(response.data.detectedUrls || []);
        setImportWarnings(response.data.warnings || []);
        setImportWarningAck(false);
        setImportModalOpen(true);
      } else {
        setImportError(response.error || 'Fehler beim Import');
      }
    } catch (err) {
      setImportError('Fehler beim Import');
    } finally {
      setImportLoading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleConfirmImport = () => {
    if (!importedContent || !currentEmail) return;

    // Replace selected URLs with placeholder
    let html = importedContent;
    selectedUrls.forEach((url) => {
      html = html.replace(
        new RegExp(url.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'), 'g'),
        '{PRODUCT_LINK}',
      );
    });

    updateCurrentEmail({ html_body: html });
    setHasImportedHtml(true);
    setImportModalOpen(false);
    setImportedContent(null);
    setDetectedUrls([]);
    setSelectedUrls([]);
    setImportWarnings([]);
    setImportWarningAck(false);

    // Switch to code mode for imported HTML
    setEditorMode('code');
    localStorage.setItem('plx-mailing-editor-mode', 'code');
  };

  const handleRefreshDoiCreative = async () => {
    if (!campaignId) return;
    setDoiCreativeRefreshing(true);
    setDoiCreativeStatus(null);
    try {
      const response = await api.post(
        `/api/plx-mailings/campaigns/${campaignId}/refresh-doi-creative`,
      );
      const status = response.data?.data?.status as string | undefined;
      const reason = response.data?.data?.reason as string | undefined;
      if (status === 'ok') {
        setDoiCreativeStatus('Creative aktualisiert');
      } else if (status === 'partial') {
        setDoiCreativeStatus('Creative aktualisiert (ohne Thumbnail)');
      } else {
        setDoiCreativeStatus(`Scrape fehlgeschlagen: ${reason || 'unbekannt'}`);
      }
    } catch (err: any) {
      setDoiCreativeStatus(`Fehler: ${err?.message || 'unbekannt'}`);
    } finally {
      setDoiCreativeRefreshing(false);
    }
  };

  const isAufzeichnungLandingUrl =
    typeof landingUrl === 'string' && /-aufzeichnung\/?($|\?|#)/i.test(landingUrl);

  // Get schedule status for client (for current email)
  const getScheduleStatusForClient = (clientCode: string): string | null => {
    if (!currentEmail?.schedule_statuses) return null;
    const pairs = currentEmail.schedule_statuses.split(',');
    for (const pair of pairs) {
      const [code, status] = pair.split(':');
      if (code === clientCode) return status;
    }
    return null;
  };

  // Render schedule status chip
  const renderScheduleStatus = (status: string | null) => {
    if (!status) return null;
    const colors: Record<string, 'default' | 'primary' | 'success' | 'error' | 'warning'> = {
      pending: 'warning',
      scheduled: 'primary',
      sent: 'success',
      failed: 'error',
      cancelled: 'default',
    };
    const labels: Record<string, string> = {
      pending: 'Wartend',
      scheduled: 'Geplant',
      sent: 'Gesendet',
      failed: 'Fehler',
      cancelled: 'Abgebrochen',
    };
    return (
      <Chip
        size="small"
        label={labels[status] || status}
        color={colors[status] || 'default'}
        sx={{ ml: 1 }}
      />
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}

      {campaignId && (sendNumber > 1 || seriesSize > 1) && (
        <Alert
          severity="info"
          icon={<ReplayIcon />}
          sx={{ mb: 2 }}
          action={
            parentCampaignId && parentName ? (
              <Link
                component="button"
                type="button"
                underline="hover"
                onClick={() => setSearchParams({ tab: 'editor', id: String(parentCampaignId) })}
              >
                Original öffnen
              </Link>
            ) : null
          }
        >
          {sendNumber > 1 ? (
            <>
              <strong>Send #{sendNumber}</strong> einer Series mit{' '}
              <strong>{seriesSize} Sends</strong>
              {parentName ? (
                <>
                  {' '}
                  — Original: <em>{parentName}</em>
                </>
              ) : null}
            </>
          ) : (
            <>
              <strong>Series-Root</strong> — diese Kampagne hat{' '}
              <strong>
                {seriesSize - 1} Resend{seriesSize - 1 === 1 ? '' : 's'}
              </strong>
              {seriesRootId ? ` (Series #${seriesRootId})` : ''}
            </>
          )}
        </Alert>
      )}

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Kampagnen-Details
        </Typography>

        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Kampagnenname"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Concept"
              value={concept}
              onChange={(e) => {
                // Auto-uppercase and remove spaces
                const value = e.target.value.toUpperCase().replace(/\s/g, '');
                if (value.length <= 10) {
                  setConcept(value);
                }
              }}
              inputProps={{ maxLength: 10 }}
              helperText="1-10 Zeichen, keine Leerzeichen (wird im Banner verwendet, z.B. CAR3)"
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth required>
              <InputLabel>Produkt</InputLabel>
              <Select
                value={productId}
                label="Produkt"
                onChange={(e) => setProductId(e.target.value as number)}
              >
                {products.map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} ({p.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>WKZ (optional)</InputLabel>
              <Select
                value={wkzCode}
                label="WKZ (optional)"
                onChange={(e) => setWkzCode(e.target.value)}
                disabled={!productId}
              >
                <MenuItem value="">Kein WKZ</MenuItem>
                {wkzConfigs.map((w) => (
                  <MenuItem key={w.wkz_code} value={w.wkz_code}>
                    {w.wkz_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="Landing-URL"
              value={landingUrl}
              onChange={(e) => setLandingUrl(e.target.value)}
              onBlur={() => validateUrl('landing_url', landingUrl)}
              required
              placeholder="https://..."
              error={landingUrlState.reachable === false && !landingUrlState.cross_domain_redirect}
              helperText={
                landingUrlState.validating
                  ? 'URL wird geprüft...'
                  : landingUrlState.reachable === true
                    ? 'URL erreichbar'
                    : landingUrlState.cross_domain_redirect
                      ? `Weiterleitung zu anderer Domain: ${landingUrlState.redirect_url}`
                      : landingUrlState.error
                        ? landingUrlState.error
                        : 'System fügt Tracking-Parameter automatisch hinzu'
              }
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    {landingUrlState.validating && <CircularProgress size={18} />}
                    {landingUrlState.reachable === true &&
                      !landingUrlState.cross_domain_redirect && (
                        <CheckCircleIcon color="success" fontSize="small" />
                      )}
                    {landingUrlState.cross_domain_redirect && (
                      <WarningIcon sx={{ color: '#ed6c02' }} fontSize="small" />
                    )}
                    {landingUrlState.reachable === false &&
                      !landingUrlState.cross_domain_redirect && (
                        <ErrorIcon color="error" fontSize="small" />
                      )}
                  </InputAdornment>
                ),
              }}
            />
            {isAufzeichnungLandingUrl && campaignId && (
              <Box mt={1} display="flex" alignItems="center" gap={1}>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={doiCreativeRefreshing}
                  onClick={handleRefreshDoiCreative}
                >
                  {doiCreativeRefreshing ? 'Aktualisiere…' : 'DOI-Creative aktualisieren'}
                </Button>
                {doiCreativeStatus && (
                  <Typography variant="caption" color="text.secondary">
                    {doiCreativeStatus}
                  </Typography>
                )}
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              type="date"
              label="Bewerben bis (einschließlich)"
              value={validUntil}
              onChange={(e) => setValidUntil(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="Leer = unbefristet. Ab dem Folgetag wählt die KI-Anzeige im Newsletter das Angebot nicht mehr."
            />
          </Grid>

          {campaignId ? (
            <Grid item xs={12}>
              <PromotionStatusPanel
                campaignId={campaignId}
                bewerbbarkeit={bewerbbarkeit}
                onChange={setBewerbbarkeit}
              />
            </Grid>
          ) : null}

          {/* Welcome-Variante: dieselbe Kampagne belegt zusätzlich eine Position
              der Welcome-Serie. Bewusst hier und nicht in einem eigenen Editor —
              Attribution, {PRODUCT_LINK} und der Welcome-Versand hängen alle an
              plx_mailing_campaigns → plx_products. */}
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel>Welcome-Position (optional)</InputLabel>
              <Select
                value={welcomePosition}
                label="Welcome-Position (optional)"
                onChange={(e) =>
                  setWelcomePosition(e.target.value === '' ? '' : Number(e.target.value))
                }
              >
                <MenuItem value="">– keine (normale PLX-Kampagne) –</MenuItem>
                {[1, 2, 3, 4, 5].map((position) => (
                  <MenuItem key={position} value={position}>
                    Mail {position} der Welcome-Serie
                  </MenuItem>
                ))}
              </Select>
              <FormHelperText component="div">
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-start' }}>
                  <span>
                    Gesetzt = diese Copy wird an neue Abonnenten auf der gewählten Position
                    ausgeliefert. Sie nimmt NICHT am wöchentlichen ETKP-Ranking teil.
                  </span>
                  <InfoTip text="Die Welcome-Serie ist die feste Mailfolge, die jeder neue Newsletter-Abonnent automatisch bekommt — die erste sofort, die weiteren an den Folgetagen. Normalerweise wählt das System die Mails montags selbst aus; eine hier gesetzte Position ersetzt diese Auswahl. Die Kampagne geht dann NUR noch an neue Abonnenten und nie über die reguläre Planung." />
                </Box>
              </FormHelperText>
            </FormControl>
          </Grid>

          {welcomePosition !== '' && (
            <Grid item xs={12} md={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={welcomeAbTest}
                    onChange={(e) => setWelcomeAbTest(e.target.checked)}
                  />
                }
                label="Als A/B-Test gegen die automatische Auswahl"
              />
              <FormHelperText>
                {welcomeAbTest
                  ? 'Die Hälfte der neuen Abonnenten bekommt diese Copy, die andere Hälfte das automatisch gewählte Mailing. Zuordnung fest je Empfänger.'
                  : 'Ohne Haken ersetzt diese Copy das automatisch gewählte Mailing für ALLE neuen Abonnenten.'}
              </FormHelperText>
            </Grid>
          )}

          {welcomePosition !== '' && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={welcomeCopyActive}
                    onChange={(e) => setWelcomeCopyActive(e.target.checked)}
                  />
                }
                label="Copy aktiv — wird an neue Abonnenten ausgeliefert"
              />
              <FormHelperText component="div">
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'flex-start' }}>
                  <span>
                    {welcomeCopyActive
                      ? 'Diese Copy geht ab sofort an neue Abonnenten.'
                      : 'Solange der Haken fehlt, läuft weiter das automatisch gewählte Mailing. Neu angelegte Copies starten inaktiv.'}
                  </span>
                  <InfoTip text="Eine frisch angelegte Copy ist zunächst eine 1:1-Kopie des automatisch gewählten Mailings. Ginge sie sofort raus, bekämen die ersten Empfänger denselben Text — bei einem A/B-Test wären deren Ergebnisse dann wertlos, ohne dass man es später noch erkennen könnte. Deshalb erst scharfschalten, wenn der Text steht. Zum Pausieren einfach wieder abwählen." />
                </Box>
              </FormHelperText>
            </Grid>
          )}

          <Grid item xs={12}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={targetClients !== null}
                  onChange={(e) => setTargetClients(e.target.checked ? [] : null)}
                />
              }
              label="Nur an bestimmte Ziel-Clients senden (sonst Standard-Versand an ft, bg, sw, be, tt, kme, dnv)"
            />
            {targetClients !== null && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, pl: 4, mt: 0.5 }}>
                {clients.map((c) => (
                  <FormControlLabel
                    key={c.code}
                    control={
                      <Checkbox
                        size="small"
                        checked={targetClients.includes(c.code)}
                        onChange={(e) =>
                          setTargetClients((prev) => {
                            const cur = prev ?? [];
                            return e.target.checked
                              ? [...cur, c.code]
                              : cur.filter((x) => x !== c.code);
                          })
                        }
                      />
                    }
                    label={`${c.name} (${c.code})`}
                  />
                ))}
              </Box>
            )}
            {targetClients !== null && targetClients.length === 0 && (
              <Typography variant="caption" color="error" sx={{ display: 'block', pl: 4 }}>
                Kein Client ausgewählt — beim Speichern wird auf Standard-Versand zurückgesetzt.
              </Typography>
            )}
          </Grid>
        </Grid>
      </Paper>

      {currentEmail && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography variant="h6">E-Mail-Inhalt</Typography>
            <Box display="flex" gap={1}>
              <input
                type="file"
                accept=".html,.htm"
                style={{ display: 'none' }}
                ref={fileInputRef}
                onChange={handleImportFile}
              />
              <Button
                startIcon={<UploadFileIcon />}
                onClick={() => fileInputRef.current?.click()}
                disabled={importLoading || isEmailSent}
              >
                HTML importieren
              </Button>
              <ToggleButtonGroup
                value={editorMode}
                exclusive
                onChange={handleEditorModeChange}
                size="small"
              >
                <ToggleButton value="wysiwyg">
                  <EditIcon sx={{ mr: 0.5 }} /> WYSIWYG
                </ToggleButton>
                <ToggleButton value="code">
                  <CodeIcon sx={{ mr: 0.5 }} /> Code
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>

          {/* Email Tabs */}
          <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
            <Box display="flex" alignItems="center">
              <Tabs
                value={activeEmailTab}
                onChange={(_, v) => setActiveEmailTab(v)}
                sx={{ flexGrow: 1 }}
              >
                {emails.map((email, idx) => {
                  const hasSent = email.schedule_statuses?.includes('sent');
                  const hasScheduled =
                    email.schedule_statuses?.split(',').some((pair: string) => {
                      const status = pair.split(':')[1];
                      return status === 'pending' || status === 'scheduled';
                    }) || false;

                  let tabIcon = undefined;
                  if (hasSent) {
                    tabIcon = <CheckCircleIcon color="success" fontSize="small" />;
                  } else if (hasScheduled) {
                    tabIcon = <ScheduleIcon sx={{ color: '#ed6c02' }} fontSize="small" />;
                  }

                  return (
                    <Tab key={idx} label={`E-Mail ${idx + 1}`} icon={tabIcon} iconPosition="end" />
                  );
                })}
              </Tabs>
              {emails.length < 5 && (
                <IconButton
                  onClick={addEmail}
                  size="small"
                  color="primary"
                  title="E-Mail hinzufügen"
                >
                  <AddIcon />
                </IconButton>
              )}
            </Box>
          </Box>

          {/* Email actions row */}
          <Box display="flex" justifyContent="flex-end" gap={1} mb={2}>
            {/* Cancel schedules button for this email (only if has active schedules) */}
            {isEmailScheduled && (
              <Button
                startIcon={<CancelIcon />}
                color="warning"
                size="small"
                onClick={() => handleCancelEmailSchedules(activeEmailTab)}
              >
                Planungen canceln
              </Button>
            )}
            {/* Remove button (only if >1 and not sent/scheduled) */}
            {emails.length > 1 && !(isEmailSent || isEmailScheduled) && (
              <Button
                startIcon={<DeleteIcon />}
                color="error"
                size="small"
                onClick={() => removeEmail(activeEmailTab)}
              >
                E-Mail entfernen
              </Button>
            )}
          </Box>

          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Betreff"
                value={currentEmail.subject}
                onChange={(e) => updateCurrentEmail({ subject: e.target.value })}
                disabled={isEmailSent || isEmailScheduled}
                error={containsHardcodedYear(currentEmail.subject)}
                helperText={containsHardcodedYear(currentEmail.subject) ? YEAR_WARNING : undefined}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Sendedatum/-zeit (MEZ/MESZ)"
                type="datetime-local"
                value={currentEmail.scheduled_datetime}
                onChange={(e) => updateCurrentEmail({ scheduled_datetime: e.target.value })}
                InputLabelProps={{ shrink: true }}
                disabled={isEmailSent || isEmailScheduled}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Absendername"
                value={currentEmail.from_name}
                onChange={(e) => updateCurrentEmail({ from_name: e.target.value })}
                disabled={isEmailSent || isEmailScheduled}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Vorschautext"
                value={currentEmail.preview_text}
                onChange={(e) => updateCurrentEmail({ preview_text: e.target.value })}
                disabled={isEmailSent || isEmailScheduled}
                error={containsHardcodedYear(currentEmail.preview_text)}
                helperText={
                  containsHardcodedYear(currentEmail.preview_text) ? YEAR_WARNING : undefined
                }
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          {/* Auto-fan-out info banner — replaces the legacy multi-client picker.
              Every PLX mailing goes to FT (Iterable) + 4 external Mailchimp
              audiences. Externals are gated to GEV ⨯ I cross-subscribers at
              schedule time (PlxMailingService.scheduleMailchimp). */}
          <Box mb={2}>
            <Alert
              severity="info"
              icon={<SendIcon fontSize="small" />}
              sx={{ '& .MuiAlert-message': { width: '100%' } }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Versand: Finanztrends + Auto-Extern (GEV ⨯ I)
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={1} mt={0.5}>
                {PLX_FANOUT_CLIENT_CODES.map((code) => {
                  const client = clients.find((c) => c.code === code);
                  const status = getScheduleStatusForClient(code);
                  const label = client?.name || code.toUpperCase();
                  const esp = client?.esp_type || (code === 'ft' ? 'iterable' : 'mailchimp');
                  return (
                    <Chip
                      key={code}
                      size="small"
                      label={
                        <Box component="span" display="inline-flex" alignItems="center">
                          {label} ({esp}){renderScheduleStatus(status)}
                        </Box>
                      }
                      color={code === 'ft' ? 'primary' : 'default'}
                      variant={code === 'ft' ? 'filled' : 'outlined'}
                    />
                  );
                })}
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                Externe Sends (BG, SW, BE, TT) gehen nur an GEV ⨯ I Doppel-Abonnenten —
                VNR-Querkunden, die ohnehin keine Extern-SAMs mehr erhalten. KME und DNV senden an
                ihre volle Liste (max. 2 Mails pro Kampagne; DNV pausiert am wöchentlichen
                SAM-Haupttag). Werbe-Sperre und Inaktive werden ausgeschlossen.
              </Typography>
            </Alert>
          </Box>

          {/* Client From Names */}
          <Box mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={currentEmail.use_client_from_names}
                  onChange={(e) =>
                    updateCurrentEmail({
                      use_client_from_names: e.target.checked,
                    })
                  }
                  disabled={isEmailSent || isEmailScheduled}
                />
              }
              label="Unterschiedliche Absendernamen pro Client"
            />
            {currentEmail.use_client_from_names && (
              <Grid container spacing={2} sx={{ mt: 1 }}>
                {currentEmail.selected_clients.map((clientCode) => {
                  const client = clients.find((c) => c.code === clientCode);
                  return (
                    <Grid item xs={12} md={4} key={clientCode}>
                      <TextField
                        fullWidth
                        size="small"
                        label={`${client?.name || clientCode} Absendername`}
                        value={
                          currentEmail.client_from_names?.[clientCode] || currentEmail.from_name
                        }
                        onChange={(e) => {
                          updateCurrentEmail({
                            client_from_names: {
                              ...currentEmail.client_from_names,
                              [clientCode]: e.target.value,
                            },
                          });
                        }}
                        disabled={isEmailSent || isEmailScheduled}
                      />
                    </Grid>
                  );
                })}
              </Grid>
            )}
          </Box>

          {/* Auto Non-Opener */}
          <Box mb={2}>
            <FormControlLabel
              control={
                <Switch
                  checked={currentEmail.auto_non_opener}
                  onChange={(e) =>
                    updateCurrentEmail({
                      auto_non_opener: e.target.checked,
                    })
                  }
                  disabled={isEmailSent || isEmailScheduled}
                />
              }
              label="Automatischer Non-Opener Versand"
            />
            {currentEmail.auto_non_opener && (
              <Box sx={{ mt: 1, ml: 2 }}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <TextField
                    type="time"
                    label="Non-Opener Uhrzeit"
                    value={currentEmail.auto_non_opener_time}
                    onChange={(e) =>
                      updateCurrentEmail({
                        auto_non_opener_time: e.target.value,
                      })
                    }
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    disabled={isEmailSent || isEmailScheduled}
                    sx={{ width: 160 }}
                  />
                </Box>
                <FormControlLabel
                  control={
                    <Switch
                      checked={currentEmail.auto_non_opener_clients === null}
                      onChange={(e) =>
                        updateCurrentEmail({
                          auto_non_opener_clients: e.target.checked
                            ? null
                            : [...currentEmail.selected_clients],
                        })
                      }
                      size="small"
                      disabled={isEmailSent || isEmailScheduled}
                    />
                  }
                  label="Alle ausgewählten Clients"
                />
                {currentEmail.auto_non_opener_clients !== null && (
                  <FormGroup row sx={{ ml: 2 }}>
                    {currentEmail.selected_clients.map((clientCode) => {
                      const client = clients.find((c) => c.code === clientCode);
                      return (
                        <FormControlLabel
                          key={clientCode}
                          control={
                            <Checkbox
                              size="small"
                              checked={
                                currentEmail.auto_non_opener_clients?.includes(clientCode) || false
                              }
                              onChange={(e) => {
                                const current = currentEmail.auto_non_opener_clients || [];
                                updateCurrentEmail({
                                  auto_non_opener_clients: e.target.checked
                                    ? [...current, clientCode]
                                    : current.filter((c) => c !== clientCode),
                                });
                              }}
                              disabled={isEmailSent || isEmailScheduled}
                            />
                          }
                          label={client?.name || clientCode}
                        />
                      );
                    })}
                  </FormGroup>
                )}
                <Alert severity="info" sx={{ mt: 1 }}>
                  <Typography variant="body2">
                    Non-Opener wird automatisch am selben Tag nach dem Original-Versand erstellt. FT
                    (Iterable): Mindestens 10 Stunden Abstand wird automatisch eingehalten.
                  </Typography>
                </Alert>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Copy-Refresh banner — only on resend children with detected stale anchors. */}
          {parentCampaignId !== null &&
            stalenessFlags &&
            stalenessFlags.totalCount > 0 &&
            !copyRefreshDismissed && (
              <Alert
                severity="warning"
                icon={<AutoFixHighIcon />}
                sx={{ mb: 2 }}
                onClose={() => setCopyRefreshDismissed(true)}
                action={
                  <Button
                    color="inherit"
                    size="small"
                    onClick={() => setCopyRefreshOpen(true)}
                    startIcon={<AutoFixHighIcon />}
                  >
                    Copy-Refresh prüfen
                  </Button>
                }
              >
                <strong>{stalenessFlags.totalCount}</strong> potenziell veraltete{' '}
                {describeStalenessKind(stalenessFlags.mostSeriousKind)} im Text erkannt
                {stalenessFlags.scheduledDatetime
                  ? ` (Sendetermin ${formatGermanTime(stalenessFlags.scheduledDatetime, 'DD.MM.YYYY HH:mm')})`
                  : ''}
                . Vor dem Resend prüfen.
              </Alert>
            )}

          {/* Placeholder Legend */}
          <PlaceholderLegend
            onInsert={(placeholder) => {
              if (editorMode === 'code' && monacoRef.current) {
                const editor = monacoRef.current;
                const selection = editor.getSelection();
                editor.executeEdits('insert', [
                  {
                    range: selection,
                    text: placeholder,
                    forceMoveMarkers: true,
                  },
                ]);
              } else if (editorRef.current) {
                editorRef.current.insertContent(placeholder);
              }
            }}
          />

          {/* HTML Editor */}
          <Box sx={{ mt: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
            {editorMode === 'wysiwyg' ? (
              <TinyMCEEditor
                ref={editorRef}
                value={currentEmail.html_body}
                onChange={(content) => updateCurrentEmail({ html_body: content })}
                disabled={isEmailSent || isEmailScheduled}
              />
            ) : (
              <Editor
                height="500px"
                defaultLanguage="html"
                value={currentEmail.html_body}
                onChange={(value) => updateCurrentEmail({ html_body: value || '' })}
                onMount={(editor) => {
                  monacoRef.current = editor;
                }}
                options={{
                  minimap: { enabled: false },
                  wordWrap: 'on',
                  readOnly: isEmailSent || isEmailScheduled,
                }}
              />
            )}
          </Box>

          {/* Validation Status */}
          <Box
            sx={{
              mt: 2,
              p: 2,
              borderRadius: 1,
              backgroundColor:
                validation.valid && validation.warnings.length === 0
                  ? '#e8f5e9'
                  : validation.valid
                    ? '#fff3e0'
                    : '#ffebee',
            }}
          >
            <Box display="flex" alignItems="center" gap={1}>
              {validation.valid && validation.warnings.length === 0 ? (
                <>
                  <CheckCircleIcon sx={{ color: '#4caf50' }} />
                  <Typography variant="body2" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>
                    Alle Prüfungen bestanden
                  </Typography>
                </>
              ) : validation.valid ? (
                <>
                  <WarningIcon sx={{ color: '#ff9800' }} />
                  <Typography variant="body2" sx={{ color: '#e65100', fontWeight: 'bold' }}>
                    Hinweise vorhanden
                  </Typography>
                </>
              ) : (
                <>
                  <ErrorIcon sx={{ color: '#f44336' }} />
                  <Typography variant="body2" sx={{ color: '#c62828', fontWeight: 'bold' }}>
                    Validierungsfehler
                  </Typography>
                </>
              )}
            </Box>

            {validation.errors.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {validation.errors.map((err, i) => (
                  <Typography key={i} variant="body2" sx={{ color: '#c62828', ml: 4 }}>
                    • {err}
                  </Typography>
                ))}
              </Box>
            )}

            {validation.warnings.length > 0 && (
              <Box sx={{ mt: 1 }}>
                {validation.warnings.map((warn, i) => (
                  <Typography key={i} variant="body2" sx={{ color: '#e65100', ml: 4 }}>
                    • {warn}
                  </Typography>
                ))}
              </Box>
            )}
          </Box>

          {/* Spam-Score / Content-Quality Panel (active email only) */}
          {currentEmail && (
            <SpamScorePanel
              editorType="plx_campaign"
              referenceId={currentEmail.mailing_number ?? null}
              campaignId={campaignId ?? null}
              subject={currentEmail.subject || ''}
              preheader={currentEmail.preview_text || ''}
              body={currentEmail.html_body || ''}
              fromName={currentEmail.from_name || ''}
              onResultChange={(r) => handleSpamResultChange(activeEmailTab, r)}
            />
          )}
        </Paper>
      )}

      {/* Non-Opener Section - only show if campaign exists and has scheduled/sent emails */}
      {campaignId && emails.some((e) => e.schedule_statuses) && (
        <Paper sx={{ p: 3, mb: 3 }}>
          <NonOpenerSection
            systemType="plx"
            campaignId={campaignId}
            emails={emails.map((e) => ({
              mailing_number: e.mailing_number,
              subject: e.subject,
              selected_clients: e.selected_clients,
            }))}
            onNonOpenerScheduled={() => loadCampaign(campaignId)}
          />
        </Paper>
      )}

      {/* Action Buttons */}
      <Box display="flex" justifyContent="space-between" gap={2}>
        <Button variant="outlined" onClick={onCancel}>
          Abbrechen
        </Button>

        <Box display="flex" gap={2}>
          {campaignId && (
            <>
              <Button
                variant="outlined"
                startIcon={<VisibilityIcon />}
                onClick={handlePreview}
                disabled={previewLoading}
              >
                Vorschau
              </Button>
              <Button
                variant="outlined"
                startIcon={<SendIcon />}
                onClick={() => setTestEmailOpen(true)}
              >
                Test senden
              </Button>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<ScheduleIcon />}
                onClick={handleSchedule}
                disabled={saving || isEmailSent || scheduleDialogOpen}
              >
                Bei ESP planen
              </Button>
              {isEmailScheduled && (
                <Button
                  variant="contained"
                  color="warning"
                  startIcon={<SendIcon />}
                  onClick={handleSendAllToEsp}
                  disabled={saving}
                >
                  Jetzt senden
                </Button>
              )}
              {hasAnyActiveSchedules && (
                <Tooltip title="Alle Planungen für diese Kampagne canceln">
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={handleCancelCampaignSchedules}
                    disabled={saving || cancelling}
                  >
                    Alle abbrechen
                  </Button>
                </Tooltip>
              )}
              <Tooltip title="Kampagne als nächsten Send im Series klonen — Inhalte werden kopiert, Schedules nicht">
                <Button
                  variant="outlined"
                  color="info"
                  startIcon={<ReplayIcon />}
                  onClick={() => setResendDialogOpen(true)}
                  disabled={saving || resendLoading}
                >
                  Erneut senden
                </Button>
              </Tooltip>
            </>
          )}
          <Button
            variant="contained"
            color="primary"
            startIcon={<SaveIcon />}
            onClick={() => handleSave()}
            disabled={saving || !validation.valid || hasUnreachableUrls() || isValidating()}
            title={!validation.valid ? 'Bitte beheben Sie die Validierungsfehler' : ''}
          >
            {saving ? <CircularProgress size={24} /> : 'Speichern'}
          </Button>
        </Box>
      </Box>

      {/* Import Modal */}
      <Dialog
        open={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>HTML importieren</DialogTitle>
        <DialogContent>
          {importWarnings.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Import unsauber — bitte prüfen</AlertTitle>
              {importWarnings.map((warning, idx) => (
                <Typography key={idx} variant="body2" sx={{ mb: 1 }}>
                  {warning}
                </Typography>
              ))}
              <FormControlLabel
                control={
                  <Checkbox
                    checked={importWarningAck}
                    onChange={(e) => setImportWarningAck(e.target.checked)}
                  />
                }
                label={
                  <Typography variant="body2">
                    Ich habe die Vorschau geprüft und will das trotzdem übernehmen
                  </Typography>
                }
              />
            </Alert>
          )}
          {detectedUrls.length > 0 && (
            <Box mb={2}>
              <Typography variant="subtitle2" gutterBottom>
                Erkannte URLs - zum Ersetzen durch {'{PRODUCT_LINK}'} auswählen:
              </Typography>
              {detectedUrls.map((url, idx) => (
                <FormControlLabel
                  key={idx}
                  control={
                    <Checkbox
                      checked={selectedUrls.includes(url)}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedUrls([...selectedUrls, url]);
                        } else {
                          setSelectedUrls(selectedUrls.filter((u) => u !== url));
                        }
                      }}
                    />
                  }
                  label={
                    <Typography variant="body2" noWrap sx={{ maxWidth: 500 }}>
                      {url}
                    </Typography>
                  }
                />
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setImportModalOpen(false)}>Abbrechen</Button>
          <Button
            onClick={handleConfirmImport}
            variant="contained"
            disabled={importWarnings.length > 0 && !importWarningAck}
          >
            Importieren
          </Button>
        </DialogActions>
      </Dialog>

      {/* Preview Modal - using shared component */}
      <EmailPreviewDialog
        open={previewOpen}
        onClose={() => setPreviewOpen(false)}
        previewHtml={previewHtml}
        subject={currentEmail?.subject}
        clients={clients.filter((c) => c.active).map((c) => ({ code: c.code, name: c.name }))}
        selectedClient={previewClient}
        onClientChange={setPreviewClient}
        loading={previewLoading}
        infoBox={
          previewWarnings.length > 0 ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Dieser Body wird so nicht versendet</AlertTitle>
              {previewWarnings.map((warning, idx) => (
                <Typography key={idx} variant="body2">
                  {warning}
                </Typography>
              ))}
            </Alert>
          ) : undefined
        }
      />

      {/* Test Email Modal - using shared component.
          defaultSelectedClients pulls from availableTestClients (already filtered
          to renderable codes), NOT raw currentEmail.selected_clients — otherwise
          codes that have no checkbox in the dialog (because they're not in the
          master client list) ride along in dialog state and end up in the POST
          body. See incident 2026-04-30: campaign with selected_clients=ft,be,bg,sw,tt
          on a dev environment whose master list only had ft sent test emails to all 5. */}
      <TestEmailDialog
        open={testEmailOpen}
        onClose={() => setTestEmailOpen(false)}
        onSend={handleSendTestEmail}
        availableClients={availableTestClients}
        defaultSelectedClients={availableTestClients.map((c) => c.code)}
        subtitle={`Sendet E-Mail #${currentEmail?.mailing_number || 1} als Test an ausgewählte Clients.`}
      />

      {/* WYSIWYG Warning Modal */}
      <Dialog open={showWysiwygWarning} onClose={() => setShowWysiwygWarning(false)}>
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <WarningIcon color="warning" sx={{ mr: 1 }} />
            Warnung
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography>
            Diese E-Mail enthält importiertes HTML. Der WYSIWYG-Editor könnte die Formatierung
            verändern. Möchten Sie trotzdem wechseln?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowWysiwygWarning(false)}>Abbrechen</Button>
          <Button
            onClick={() => {
              setEditorMode('wysiwyg');
              localStorage.setItem('plx-mailing-editor-mode', 'wysiwyg');
              setShowWysiwygWarning(false);
            }}
            variant="contained"
            color="warning"
          >
            Trotzdem wechseln
          </Button>
        </DialogActions>
      </Dialog>

      {/* Schedule Confirmation Dialog */}
      <ScheduleConfirmationDialog
        open={scheduleDialogOpen}
        onClose={() => setScheduleDialogOpen(false)}
        system="plx"
        policy={mailingPolicy}
        emails={emails.map((e) => ({
          mailing_number: e.mailing_number,
          subject: e.subject,
          scheduled_datetime: e.scheduled_datetime,
          // Reflect the campaign's effective fan-out: target_clients override
          // when set, otherwise the standard auto-fan-out list.
          selected_clients:
            targetClients && targetClients.length > 0
              ? targetClients
              : [...PLX_FANOUT_CLIENT_CODES],
          from_name: e.from_name,
          use_client_from_names: e.use_client_from_names,
          client_from_names: e.client_from_names,
          schedule_statuses: e.schedule_statuses,
          status: e.status,
        }))}
        clients={clients.map((c) => ({
          code: c.code,
          name: c.name,
          esp_type: c.esp_type,
        }))}
        campaignName={name}
        isSchedulable={isEmailSchedulable}
        formatScheduledTime={(email) => formatGermanTime(email.scheduled_datetime, 'DD.MM. HH:mm')}
        onSchedule={executeSchedule}
        onSuccess={() => {
          setScheduleDialogOpen(false);
          setSuccess('E-Mails geplant — ESP-Versand läuft im Hintergrund');
          loadCampaign(campaignId!);
        }}
        showIterableWarning
      />

      {/* Cancel Schedules Confirmation Dialog */}
      {/* Resend confirmation dialog */}
      <Dialog
        open={resendDialogOpen}
        onClose={() => !resendLoading && setResendDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Erneut senden — als nächsten Send im Series anlegen?</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Es wird eine neue Kampagne als <strong>Send #{seriesSize + 1}</strong> dieser Serie
            angelegt. Alle E-Mails (Betreff, HTML, From-Name-Overrides, Client-Auswahl,
            Auto-Non-Opener) werden kopiert. Schedules und Suppression-Listen werden{' '}
            <strong>nicht</strong> kopiert — die werden bei der Planung neu gebaut. Anschließend
            kannst du zeitkritische Inhalte (Datum, Preis, „heute&ldquo;-Bezüge) anpassen.
          </DialogContentText>
          <Alert severity="info" sx={{ mb: 2 }}>
            Falls du im Editor ungespeicherte Änderungen hast, werden die zuerst gespeichert. Sonst
            wird der zuletzt gespeicherte Stand geklont. Bei Speicher-Fehler (Validierung, URL,
            Spam-Block) wird der Resend abgebrochen.
          </Alert>
          <TextField
            label="Erste E-Mail versenden am"
            type="datetime-local"
            value={resendFirstSendDate}
            onChange={(e) => setResendFirstSendDate(e.target.value)}
            InputLabelProps={{ shrink: true }}
            helperText="Erste E-Mail des Resend wird zu diesem Zeitpunkt versendet. Alle weiteren E-Mails verschieben sich im gleichen Verhältnis (Stunden/Tage zwischen den E-Mails bleiben erhalten)."
            fullWidth
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResendDialogOpen(false)} disabled={resendLoading}>
            Abbrechen
          </Button>
          <Button
            onClick={confirmResend}
            color="info"
            variant="contained"
            startIcon={<ReplayIcon />}
            disabled={resendLoading}
          >
            {resendLoading ? <CircularProgress size={20} /> : `Send #${seriesSize + 1} anlegen`}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={cancelDialogOpen} onClose={() => setCancelDialogOpen(false)}>
        <DialogTitle>
          {cancelScope === 'campaign'
            ? 'Alle Planungen canceln?'
            : `Planungen für E-Mail ${cancelEmailId !== null ? cancelEmailId + 1 : ''} canceln?`}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {cancelScope === 'campaign'
              ? 'Alle geplanten Versendungen für diese Kampagne werden in den ESPs abgebrochen und aus der Datenbank gelöscht. Bereits versendete E-Mails bleiben erhalten.'
              : 'Alle geplanten Versendungen für diese E-Mail werden in den ESPs abgebrochen und aus der Datenbank gelöscht. Bereits versendete E-Mails bleiben erhalten.'}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCancelDialogOpen(false)}>Abbrechen</Button>
          <Button
            onClick={executeCancelSchedules}
            color="error"
            variant="contained"
            disabled={cancelling}
          >
            {cancelling ? 'Wird abgebrochen...' : 'Planungen canceln'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spam-Score Hard-Block Override Dialog */}
      <Dialog
        open={spamOverrideOpen}
        onClose={() => {
          spamOverridePayload?.resolve(false);
          setSpamOverrideOpen(false);
          setSpamOverridePayload(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#dc2626', fontWeight: 'bold' }}>
          ⚠️ Content-Quality-Check: kritischer Score
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {spamOverridePayload != null && (
              <>
                E-Mail {spamOverridePayload.emailIndex + 1} enthält{' '}
                <strong>
                  {spamResults[spamOverridePayload.emailIndex]?.layer1.redCount ?? 0}{' '}
                  Red-Flag-Wörter
                </strong>
                . Diese Kombination erhöht das Risiko als Spam eingestuft zu werden spürbar.
                <br />
                <br />
                Du kannst die Mail trotzdem speichern — die Entscheidung wird zu Auswertungs-Zwecken
                protokolliert. Alternativ: Text überarbeiten und neu speichern.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              spamOverridePayload?.resolve(false);
              setSpamOverrideOpen(false);
              setSpamOverridePayload(null);
            }}
          >
            Abbrechen & überarbeiten
          </Button>
          <Button
            variant="contained"
            color="warning"
            onClick={() => {
              spamOverridePayload?.resolve(true);
              setSpamOverrideOpen(false);
              setSpamOverridePayload(null);
            }}
          >
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Spam-Score Advisory Dialog (non-blocking) */}
      <Dialog
        open={spamAdvisoryOpen}
        onClose={() => {
          spamAdvisoryPayload?.resolve(false);
          setSpamAdvisoryOpen(false);
          setSpamAdvisoryPayload(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ color: '#f97316', fontWeight: 'bold' }}>
          💡 Content-Hinweise vor dem Speichern
        </DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {spamAdvisoryPayload != null && (
              <>
                E-Mail {spamAdvisoryPayload.emailIndex + 1} hat{' '}
                <strong>
                  {spamAdvisoryPayload.warningCount} Content-Hinweis
                  {spamAdvisoryPayload.warningCount === 1 ? '' : 'e'}
                </strong>{' '}
                (z.B. Red-Flag-Wörter, Urgency-Muster oder Caps-Runs).
                <br />
                <br />
                <strong>Empfehlung vor dem Versand:</strong>
                <ol style={{ marginTop: 4, marginBottom: 4, paddingLeft: 20 }}>
                  <li>Die markierten Hinweise im Content-Quality-Panel prüfen und bereinigen.</li>
                  {!spamAdvisoryPayload.llmChecked && (
                    <li>
                      <strong>LLM-Check starten</strong> — liefert kontextuelle Analyse + konkrete
                      Umformulierungs-Vorschläge.
                    </li>
                  )}
                  {spamAdvisoryPayload.llmChecked && (
                    <li>LLM-Rewrite-Vorschläge übernehmen und danach Check wiederholen.</li>
                  )}
                </ol>
                Bei niedriger Absender-Reputation erhöhen diese Signale das Spam-Risiko bei Gmail
                und GMX. Du kannst trotzdem speichern — die Entscheidung wird protokolliert.
              </>
            )}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              spamAdvisoryPayload?.resolve(false);
              setSpamAdvisoryOpen(false);
              setSpamAdvisoryPayload(null);
            }}
          >
            Zurück & überarbeiten
          </Button>
          <Button
            onClick={() => {
              spamAdvisoryPayload?.resolve(true);
              setSpamAdvisoryOpen(false);
              setSpamAdvisoryPayload(null);
            }}
          >
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={diversityOpen}
        onClose={() => {
          diversityPayload?.resolve(false);
          setDiversityOpen(false);
          setDiversityPayload(null);
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Ähnliche Mail in der Welcome-Serie</DialogTitle>
        <DialogContent>
          <DialogContentText component="div">
            {diversityPayload?.message}
            <br />
            <br />
            Neue Abonnenten bekommen die Serie an aufeinanderfolgenden Tagen. Zweimal dasselbe
            Produkt oder Thema kurz hintereinander wirkt schnell wie eine Wiederholung. Speichern
            ist trotzdem möglich, wenn das so gewollt ist.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            variant="contained"
            onClick={() => {
              diversityPayload?.resolve(false);
              setDiversityOpen(false);
              setDiversityPayload(null);
            }}
          >
            Zurück & ändern
          </Button>
          <Button
            onClick={() => {
              diversityPayload?.resolve(true);
              setDiversityOpen(false);
              setDiversityPayload(null);
            }}
          >
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>

      {/* Copy-Refresh side panel — LLM-grounded refresh proposal flow.
          onApplied: write the returned subject/preview/html_body back into the
          editor state so the user sees the refreshed copy immediately AND mark
          the banner as resolved for this session. */}
      <CopyRefreshPanel
        open={copyRefreshOpen}
        campaignId={campaignId || 0}
        emailId={currentEmail?.id}
        scheduledDatetime={
          stalenessFlags?.scheduledDatetime || currentEmail?.scheduled_datetime || ''
        }
        onClose={() => setCopyRefreshOpen(false)}
        onApplied={(result) => {
          updateCurrentEmail({
            html_body: result.updatedHtmlBody,
            subject: result.updatedSubject,
            preview_text: result.updatedPreviewText,
          });
          // Push the new content into the live TinyMCE editor so the user can
          // see the changes without a manual page-reload. The wrapper's
          // setContent already passes { format: 'raw' } to preserve inline styles.
          if (editorRef.current) {
            editorRef.current.setContent(result.updatedHtmlBody);
          }
          setCopyRefreshDismissed(true);
          setSuccess(
            `Copy-Refresh angewendet — ${result.appliedCount} von ${
              result.appliedCount + result.rejectedCount
            } Änderungen übernommen.`,
          );
        }}
      />
    </Box>
  );
};

export default CampaignEditor;
